import { Clock, Circle, ExternalLink } from "lucide-react";
import ResourceCard, { primaryFlags, usePrimaryZone } from "@/components/vetting/ResourceCard";

const PLACEHOLDER = /\b(video|link|image|resource|recording|example)s?\s+(to come|coming( soon)?|tbc|tba)\b/i;
const BULLET = /^\s*(•|▪|-|–|☐)\s*/;

// Runs -> lines of segments, keeping bold / italic / link per segment.
function toLines(runs) {
  const lines = [[]];
  runs.forEach((r) => {
    r.t.split("\n").forEach((part, i) => {
      if (i > 0) lines.push([]);
      if (part) lines[lines.length - 1].push({ ...r, t: part });
    });
  });
  return lines;
}

function Seg({ s }) {
  const cls = `${s.b ? "font-semibold text-slate-900" : ""} ${s.i ? "italic" : ""}`;
  if (s.u) {
    return (
      <a href={s.u} target="_blank" rel="noopener noreferrer" className={`${cls} text-[#0A1F44] underline decoration-[#C9A961] underline-offset-4 inline-flex items-center gap-0.5`}>
        {s.t}<ExternalLink className="w-3 h-3" />
      </a>
    );
  }
  return <span className={cls}>{s.t}</span>;
}

export function Unavailable({ text, testid }) {
  return (
    <div className="inline-flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid={testid}>
      <Clock className="w-4 h-4" /> {text.trim()} <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Not available yet</span>
    </div>
  );
}

const TEXT = "text-[16px] sm:text-[15px] text-slate-800 leading-7";

export default function RichBlock({ runs, checklist = false, testid }) {
  const zone = usePrimaryZone();
  const items = [];
  let para = null;
  let prevText = "";
  const flush = () => { if (para) { items.push(para); para = null; } };
  toLines(runs).forEach((line) => {
    const text = line.map((s) => s.t).join("");
    if (!text.trim()) return flush();
    // A bare-URL link label takes the plain line above it as its title (e.g. "OPTION 2 - ... LAUNCH PACKS").
    const linked = line.filter((s) => s.u).map((s) => (/^\s*https?:\/\//.test(s.t) && prevText ? { ...s, t: prevText } : s));
    prevText = line.some((s) => s.u) ? "" : text.trim();
    // A line that is only links (optionally prefixed "Resource:") becomes compact resource rows.
    const rest = line.filter((s) => !s.u).map((s) => s.t).join("").replace(/^\s*resources?\s*:?\s*/i, "");
    if (linked.length && !rest.trim()) {
      flush();
      linked.forEach((s) => items.push({ type: "resource", seg: s }));
    } else if (PLACEHOLDER.test(text) && !linked.length) {
      flush();
      items.push({ type: "placeholder", text });
    } else if (checklist && !linked.length && /;\s/.test(text)) {
      flush();
      text.split(/;\s+/).map((t) => t.trim().replace(/[;.]$/, "")).filter(Boolean)
        .forEach((t) => items.push({ type: "bullet", segs: [{ t: t.charAt(0).toUpperCase() + t.slice(1) }] }));
    } else if (BULLET.test(text)) {
      flush();
      const first = { ...line[0], t: line[0].t.replace(BULLET, "") };
      items.push({ type: "bullet", segs: [first, ...line.slice(1)] });
    } else {
      if (!para) para = { type: "para", lines: [] };
      para.lines.push(line);
    }
  });
  flush();
  const resItems = items.filter((it) => it.type === "resource");
  const flags = primaryFlags(resItems.map((it) => it.seg), zone);
  resItems.forEach((it, i) => { it.primary = flags[i]; });

  const out = [];
  let resources = [];
  const pushResources = () => {
    if (resources.length) {
      out.push(<div key={`r${out.length}`} className="space-y-1.5">{resources}</div>);
      resources = [];
    }
  };
  items.forEach((it, i) => {
    if (it.type === "resource") {
      resources.push(<ResourceCard key={i} seg={it.seg} primary={it.primary} testid={`${testid}-res-${i}`} />);
      return;
    }
    pushResources();
    if (it.type === "placeholder") out.push(<Unavailable key={i} text={it.text} testid={`${testid}-unavailable-${i}`} />);
    else if (it.type === "bullet") {
      out.push(
        <div key={i} className={`flex gap-2.5 ${TEXT}`}>
          {checklist ? <Circle className="w-4 h-4 text-[#8A6D1F] mt-1.5 shrink-0" /> : <span className="w-1.5 h-1.5 rounded-full bg-[#8A6D1F] mt-3 shrink-0" />}
          <span className="min-w-0 [overflow-wrap:anywhere]">{it.segs.map((s, j) => <Seg key={j} s={s} />)}</span>
        </div>,
      );
    } else {
      out.push(
        <p key={i} className={`${TEXT} [overflow-wrap:anywhere]`}>
          {it.lines.map((l, j) => <span key={j}>{j > 0 && <br />}{l.map((s, k) => <Seg key={k} s={s} />)}</span>)}
        </p>,
      );
    }
  });
  pushResources();
  return <div className="space-y-3" data-testid={testid}>{out}</div>;
}
